import Chart from "react-apexcharts";
import { LineChartOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";

import ChartCard from "../ChartCard";
import ExportDropdown from "../ExportDropDown";
import { useTheme } from "../../hooks";
import { getAccentColor } from "../../utils/helpers";
import { columnWidthFor } from "../../utils/chart";
import { buildDailySeries, faDate, rangeLength } from "../../utils/dashboard";

const CHART_ID = "dashboard-flow-chart";

/**
 * Completed-per-day on its own cannot say whether the work is keeping up. Drawn
 * against the tasks created the same day, the gap between the two series is the
 * queue: bars consistently above the line mean the backlog is growing.
 *
 * Takes the two sparse day→count maps rather than a whole payload, because the
 * organization and the personal endpoints nest them at different paths.
 */
const TaskFlowChart = ({
  completedByDay,
  createdByDay,
  range,
  loading,
  title = "جریان کار روزانه",
  hint = "ستون‌ها تسک‌های تازه‌ی هر روز و خط، تسک‌های تمام‌شده‌ی همان روز است. اگر ستون‌ها مدام بالاتر از خط بمانند، صف کار در حال بلند شدن است.",
  emptyText = "در این بازه تسکی ایجاد یا تکمیل نشده است.",
}) => {
  const organization = useSelector(({ auth }) => auth.organization);
  const { isDark, chartColors, stateColors } = useTheme();

  const completed = buildDailySeries(range, completedByDay);
  const created = buildDailySeries(range, createdByDay);

  const days = Object.keys(completed);
  const completedValues = Object.values(completed);
  const createdValues = Object.values(created);

  const isEmpty =
    completedValues.every((value) => value === 0) &&
    createdValues.every((value) => value === 0);

  const accent = getAccentColor(organization);

  return (
    <ChartCard
      icon={<LineChartOutlined />}
      tone="blue"
      title={title}
      hint={hint}
      loading={loading}
      isEmpty={isEmpty}
      emptyText={emptyText}
      action={
        <ExportDropdown
          chartId={CHART_ID}
          handleExcel={() => {
            const rows = [
              ["تاریخ", "ایجادشده", "تکمیل‌شده"],
              ...days.map((day) => [faDate(day), created[day], completed[day]]),
            ];
            const ws = XLSX.utils.aoa_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "جریان کار");
            XLSX.writeFile(wb, `dashboard-flow-${new Date().toISOString()}.xlsx`);
          }}
        />
      }
    >
      <Chart
        id={CHART_ID}
        type="line"
        height={310}
        series={[
          { name: "ایجادشده", type: "column", data: createdValues },
          { name: "تکمیل‌شده", type: "line", data: completedValues },
        ]}
        options={{
          chart: {
            id: CHART_ID,
            background: "transparent",
            foreColor: chartColors.foreColor,
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: "iranyekan",
          },
          colors: [accent, stateColors.done],
          stroke: { width: [0, 3], curve: "smooth" },
          plotOptions: {
            bar: {
              borderRadius: 3,
              borderRadiusApplication: "end",
              columnWidth: columnWidthFor(days.length),
            },
          },
          fill: { opacity: [0.55, 1] },
          markers: { size: 0, hover: { size: 5 } },
          dataLabels: { enabled: false },
          legend: {
            position: "top",
            horizontalAlign: "left",
            labels: { colors: chartColors.foreColor },
            markers: { radius: 3 },
          },
          grid: {
            borderColor: chartColors.gridColor,
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
          },
          xaxis: {
            categories: days.map(faDate),
            tickAmount: Math.min(rangeLength(range) - 1, 6),
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
              rotate: 0,
              rotateAlways: false,
              trim: false,
              maxHeight: 36,
              style: { colors: chartColors.foreColor, fontSize: "11px" },
            },
          },
          yaxis: {
            min: 0,
            tickAmount: 4,
            labels: {
              style: { colors: chartColors.foreColor, fontSize: "11px" },
              formatter: (value) =>
                Number.isInteger(value) ? value.toLocaleString("fa-IR") : "",
            },
          },
          // `shared` and `intersect` are mutually exclusive in ApexCharts and the
          // combo chart defaults `intersect` on because of the column series.
          tooltip: {
            theme: isDark ? "dark" : "light",
            shared: true,
            intersect: false,
          },
          responsive: [
            { breakpoint: 576, options: { xaxis: { tickAmount: 3 } } },
          ],
        }}
        width="100%"
      />
    </ChartCard>
  );
};

export default TaskFlowChart;
